/**
 * UX02 · Panel de detalle de un indicador del Cockpit. Muestra las filas
 * EXACTAS que componen la métrica (nº y suma coinciden con el tile), con su
 * ámbito y un acceso a abrir cada registro o el módulo completo.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { X, Loader2, ArrowRight, ExternalLink } from "lucide-react";
import { API } from "@/context/AuthContext";

const fmtMM = (v) => v == null ? "—" : `${Number(v).toLocaleString("es-ES", { maximumFractionDigits: 1 })} M€`;

export function CockpitDetalleDrawer({ metric, scope, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    if (!metric) return;
    setLoading(true); setData(null);
    const p = new URLSearchParams({ metric });
    if (scope?.year) p.set("year", scope.year);
    if (scope?.dg) p.set("dg", scope.dg);
    if (scope?.zona) p.set("zona", scope.zona);
    axios.get(`${API}/cockpit/detalle?${p.toString()}`)
      .then(({ data }) => setData(data))
      .finally(() => setLoading(false));
  }, [metric, scope?.year, scope?.dg, scope?.zona]);

  if (!metric) return null;

  const openFicha = (id) => { onClose(); navigate(`/ofertas-comercial?tab=list&open=${id}`); };

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="cockpit-detalle-drawer">
      <div className="absolute inset-0 bg-black/20" onClick={onClose} />
      <aside className="relative w-full sm:w-[560px] max-w-full bg-slate-50 h-full shadow-2xl flex flex-col">
        <header className="bg-white border-b border-slate-100 px-5 py-4 flex items-start justify-between">
          <div>
            <p className="text-base font-bold text-slate-800" data-testid="detalle-titulo">{data?.metric_label || "Detalle"}</p>
            {data && (
              <p className="text-xs text-slate-500 mt-0.5" data-testid="detalle-total">
                <span className="font-semibold text-slate-700">{data.total_n}</span> operaciones ·
                <span className="font-semibold text-slate-700"> {fmtMM(data.total_mm)}</span>
              </p>
            )}
            {data?.ambito && (
              <p className="text-[11px] text-slate-400 mt-0.5">
                Ámbito: {data.ambito.year || "todos los años"}{data.ambito.dg ? ` · ${data.ambito.dg}` : ""}{data.ambito.zona ? ` · ${data.ambito.zona}` : " · todas las zonas"}
              </p>
            )}
          </div>
          <button onClick={onClose} data-testid="detalle-close" className="text-slate-400 hover:text-slate-700"><X size={20} /></button>
        </header>

        <div className="flex-1 overflow-y-auto">
          {loading && <div className="flex items-center gap-2 text-sm text-slate-400 p-6"><Loader2 className="animate-spin" size={16} /> Cargando filas…</div>}
          {data && data.rows.length === 0 && <p className="p-6 text-sm text-slate-400 italic">Sin operaciones en este ámbito.</p>}
          {data && data.rows.length > 0 && (
            <table className="w-full text-xs">
              <thead className="bg-slate-100 text-slate-400 sticky top-0">
                <tr>
                  <th className="text-left px-4 py-2 font-medium">Cuenta / objeto</th>
                  <th className="text-left px-2 py-2 font-medium">Estado</th>
                  <th className="text-right px-4 py-2 font-medium">Importe</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {data.rows.map((r, i) => (
                  <tr key={r.id || i} onClick={() => r.id && openFicha(r.id)} data-testid={`detalle-row-${i}`}
                    className="hover:bg-white cursor-pointer">
                    <td className="px-4 py-2.5">
                      <p className="font-semibold text-slate-700 truncate max-w-[240px]">{r.nombre}</p>
                      <p className="text-slate-400 truncate max-w-[240px]">{r.objeto || r.expediente || "—"}</p>
                      <span className="text-[10px] text-slate-400">{[r.dg, r.zona].filter(Boolean).join(" · ")}</span>
                    </td>
                    <td className="px-2 py-2.5 text-slate-500">{r.estado}</td>
                    <td className="px-4 py-2.5 text-right">
                      <span className="font-semibold text-slate-700">{fmtMM(r.importe_mm)}</span>
                      <ArrowRight size={11} className="inline ml-1 text-slate-300" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {data?.truncado && <p className="px-4 py-3 text-[11px] text-slate-400">Mostrando las 300 de mayor importe.</p>}
        </div>

        {data?.modulo && (
          <div className="border-t border-slate-100 bg-white p-3">
            <button onClick={() => { onClose(); navigate(data.modulo.destino); }} data-testid="detalle-abrir-modulo"
              className="w-full flex items-center justify-center gap-2 text-sm font-semibold text-indigo-600 border border-indigo-200 rounded-lg py-2 hover:bg-indigo-50">
              <ExternalLink size={14} /> {data.modulo.label}
            </button>
          </div>
        )}
      </aside>
    </div>
  );
}
